import React from 'react';
import Moment from 'moment';
import * as Sentry from '@sentry/react';

import { Box, Divider, styled } from '@mui/material';
import Coverages from '../../_statics/250/coverages/coverages.statics.json';
import GroupNameMap from '../../_statics/250/coverages/groups.statics.json';
import CheckBoxSelectedIcon from '../../../_assets/svg/CheckBoxSelectedIcon.svg';
import SimpleTooltip from '../../../components/SimpleTooltip';
import { useCoverages } from '../../../components/hooks/coverages/useCoverages';
import { ProductTypes } from '../../../types';
import LoadingSpinner from '../../workflows/bind-quote/LoadingSpinner';
import { moneyFormat } from '../../../utils/appUtils';

export default function PolicyHolderQuotePolicyP250Coverages({
  coverages,
}: any) {
  const { coverages: policyCoverages, isLoading } = usePolicyCoverages({
    coverages,
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!policyCoverages) {
    return null;
  }

  return (
    <>
      <HeaderContainer>
        <Header>Coverage Details And Limits</Header>
      </HeaderContainer>
      {Object.keys(policyCoverages).map((groupId) => {
        return (
          <React.Fragment key={groupId}>
            <HeaderContainer>
              <Divider />
              <HeaderRow>
                <HeaderCell width="37%" justifyContent="flex-end">
                  <HeaderText>{GroupNameMap[groupId].name}</HeaderText>
                </HeaderCell>
                <DividerWithPadding padding={0} />
                {!policyCoverages[groupId].isEndorsement && (
                  <>
                    <HeaderCell width="17%">
                      <HeaderText>Coverage Limit</HeaderText>
                    </HeaderCell>
                    <HeaderCell width="17%">
                      <HeaderText>Deductible</HeaderText>
                    </HeaderCell>
                    <HeaderCell width="12%">
                      <HeaderText>Waiting Period</HeaderText>
                    </HeaderCell>
                    <HeaderCell width="17%">
                      <HeaderText>Retro Active Period</HeaderText>
                    </HeaderCell>
                  </>
                )}
              </HeaderRow>
            </HeaderContainer>
            <BodyContainer>
              {Object.keys(policyCoverages[groupId]).map((coverageName) => {
                if (coverageName === 'isEndorsement') {
                  return null;
                }
                return (
                  <BodyRow key={coverageName}>
                    <BodyCell
                      width="37%"
                      justifyContent="flex-end"
                      style={{ textAlign: 'right' }}
                    >
                      <SimpleTooltip
                        title={policyCoverages[groupId][coverageName].tooltip}
                      >
                        <Text>{coverageName}</Text>
                      </SimpleTooltip>
                    </BodyCell>
                    <DividerWithPadding padding={0} />
                    {!policyCoverages[groupId][coverageName].isEndorsement ? (
                      <>
                        <BodyCell width="17%">
                          <Text>
                            {policyCoverages[groupId][coverageName].limit ||
                              '-'}
                          </Text>
                        </BodyCell>
                        <BodyCell width="17%">
                          <Text>
                            {policyCoverages[groupId][coverageName]
                              .deductible || '-'}
                          </Text>
                        </BodyCell>
                        <BodyCell width="12%" justifyContent="center">
                          <Text>
                            {policyCoverages[groupId][coverageName]
                              .waitingPeriod || '-'}
                          </Text>
                        </BodyCell>
                        <BodyCell width="17%" justifyContent="center">
                          <Text>
                            {policyCoverages[groupId][coverageName]
                              .retroActivePeriod || '-'}
                          </Text>
                        </BodyCell>
                      </>
                    ) : (
                      <BodyCell width="17%">
                        <Text>
                          <CheckBoxSelectedIcon />
                        </Text>
                      </BodyCell>
                    )}
                  </BodyRow>
                );
              })}
            </BodyContainer>
          </React.Fragment>
        );
      })}
    </>
  );
}

const DividerWithPadding = ({ padding = 1, orientation = 'vertical' }) => {
  return (
    <FlexBox pt={padding} pb={padding}>
      {/* @ts-expect-error TS(2769): No overload matches this call. */}
      <Divider orientation={orientation} flexItem />
    </FlexBox>
  );
};

const HeaderContainer = styled(Box)(({ theme: { palette } }) => ({
  backgroundColor: palette.background.default,
  width: '100%',
  borderTopRightRadius: 4,
  borderTopLeftRadius: 4,
  padding: '0',
}));

const BodyContainer = styled(Box)({
  width: '100%',
  borderBottomRightRadius: 4,
  borderBottomLeftRadius: 4,
  padding: 0,
  overflow: 'hidden',
});

const Header = styled('h3')(({ theme: { config } }) => ({
  margin: 0,
  fontSize: config.textSizes.tertia,
  fontWeight: config.weights.normal,
  padding: '0.5rem',
}));

const FlexBox = styled(Box)(() => ({
  display: 'flex',
}));

const HeaderRow = styled(FlexBox)(({ theme: { palette } }) => ({
  display: 'flex',
  width: '100%',
  minHeight: '4rem',
  backgroundColor: palette.background.default,
}));

// @ts-expect-error TS(2769): No overload matches this call.
const HeaderCell = styled(FlexBox)(({ justifyContent, theme: { config } }) => ({
  textAlign: 'center',
  alignItems: 'center',
  fontSize: config.textSizes.tertia,
  lineHeight: 1.2,
  justifyContent: justifyContent || 'center',
}));

const BodyCell = styled(HeaderCell)(({ theme: { config } }) => ({
  fontSize: config.textSizes.normal,
  textAlign: 'left',
}));

const Text = styled('div')({
  margin: '0.5rem 0.75rem',
  textTransform: 'capitalize',
});

const HeaderText = styled('div')({
  margin: '0.65rem 1rem',
});

const BodyRow = styled(HeaderRow)(({ theme: { palette } }) => ({
  minHeight: 'unset',
  '&:nth-child(2n + 1)': {
    backgroundColor: palette.background.connectorQuickView,
  },
  '&:nth-child(2n + 2)': {
    backgroundColor: palette.background.default,
  },
}));

const formatRetroActivePeriod = (
  retroActivePeriod: any,
  retroActiveDate: any
) => {
  if (retroActivePeriod === -1) {
    return 'Full';
  }
  if (retroActiveDate) {
    return Moment(retroActiveDate).utc().format('MM/DD/YY');
  }
  return '-';
};

const formatWaitingPeriod = (wait: any) => {
  if (wait < 2) {
    return `${wait} hour`;
  }
  if (wait > 1) {
    return `${wait} hours`;
  }
  return '-';
};

const usePolicyCoverages = ({ coverages }: any) => {
  // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
  const { data: availableCoverages, isInitialLoading } = useCoverages(
    ProductTypes.p250
  );

  return React.useMemo(() => {
    if (!coverages || !availableCoverages) {
      return {
        finalCoverages: null,
        isLoading: isInitialLoading,
      };
    }

    const policyCoveragesObj = coverages.reduce(
      (a, v) => ({
        ...a,

        [v.name]: {
          ...v,
          limit: v.limit ? moneyFormat(v.limit) : '-',
          deductible: v.deductible ? moneyFormat(v.deductible) : '-',
          waitingPeriod: formatWaitingPeriod(v.waitingPeriod),
          // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
          retroActivePeriod: formatRetroActivePeriod(v.retroActivePeriod),
          isSelected: v.isSelected,
        },
      }),
      {}
    );

    const reduxCoverages = {};
    Object.keys(availableCoverages).forEach((coverage) => {
      reduxCoverages[availableCoverages[coverage].key] = {
        name: availableCoverages[coverage].name,
        tooltip: availableCoverages[coverage].tooltip,
      };
    });

    const finalCoverages = Coverages.reduce((acc, coverage) => {
      if (!(coverage.key in policyCoveragesObj)) {
        return acc;
      }

      const name = reduxCoverages[coverage.key]?.name;

      if (!name) {
        Sentry.captureException(
          new Error(
            `${PolicyHolderQuotePolicyP250Coverages.name}: "name" missing for ${coverage.key}`
          )
        );
      }

      if (acc[coverage.groupId]) {
        return {
          ...acc,
          [coverage.groupId]: {
            ...acc[coverage.groupId],
            [name]: {
              ...policyCoveragesObj[coverage.key],
              tooltip: reduxCoverages[coverage.key].tooltip,
              isEndorsement: coverage.endorsement,
            },
            isEndorsement: coverage.endorsement,
          },
        };
      }

      return {
        ...acc,
        [coverage.groupId]: {
          [name]: {
            ...policyCoveragesObj[coverage.key],
            tooltip: reduxCoverages[coverage.key].tooltip,
            isEndorsement: coverage.endorsement,
          },
          isEndorsement: coverage.endorsement,
        },
      };
    }, {});

    return { coverages: finalCoverages, isLoading: isInitialLoading };
  }, [coverages, availableCoverages, isInitialLoading]);
};
